import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileSrc } from "@/lib/api";
import { PageShell } from "@/components/site/PageShell";
import { Reveal } from "@/components/site/Reveal";
import { useSeo } from "@/components/site/Seo";

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "");

export const Cover = ({ src, title, alt, className = "", eager = false }) =>
  src ? (
    <img src={fileSrc(src)} alt={alt || title} className={`h-full w-full object-cover ${className}`} loading={eager ? "eager" : "lazy"} />
  ) : (
    <div className={`grid h-full w-full place-items-end bg-[radial-gradient(circle_at_80%_20%,rgba(255,194,26,0.45),transparent_60%)] bg-surface p-6 ${className}`}>
      <span className="font-display text-6xl font-bold text-outline">bp.</span>
    </div>
  );

export const ArticleCard = ({ a, testId }) => (
  <Link to={`/blog/${a.slug}`} data-testid={testId || `blog-card-${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl bg-surface ring-1 ring-white/[0.07] transition-colors hover:ring-volt/50">
    <div className="aspect-[16/10] overflow-hidden"><Cover src={a.cover_url} title={a.title} alt={a.cover_alt} className="transition-transform duration-700 group-hover:scale-105" /></div>
    <div className="flex flex-1 flex-col p-6 md:p-7">
      <p className="font-mono text-xs text-zinc-400">{a.category ? <span className="text-volt">{a.category} · </span> : null}{fmtDate(a.created_at)}</p>
      <h2 className="mt-3 font-display text-lg font-semibold leading-snug text-paper md:text-xl">{a.title}</h2>
      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-zinc-300">{a.excerpt}</p>
      <span className="mt-auto inline-flex items-center gap-1 pt-6 text-sm font-semibold text-volt">Baca <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></span>
    </div>
  </Link>
);

const Featured = ({ a }) => (
  <Link to={`/blog/${a.slug}`} data-testid="blog-featured" className="group grid overflow-hidden rounded-[2rem] bg-surface ring-1 ring-white/[0.07] transition-colors hover:ring-volt/50 lg:grid-cols-[1.25fr_1fr]">
    <div className="aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[380px]"><Cover src={a.cover_url} title={a.title} alt={a.cover_alt} eager className="transition-transform duration-700 group-hover:scale-105" /></div>
    <div className="flex flex-col justify-center p-7 md:p-10">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-volt">Terbaru{a.category ? ` · ${a.category}` : ""}</p>
      <h2 className="mt-4 font-display text-2xl font-bold leading-tight text-paper md:text-3xl">{a.title}</h2>
      <p className="mt-4 text-zinc-300">{a.excerpt}</p>
      <p className="mt-6 font-mono text-xs text-zinc-400">{fmtDate(a.created_at)}</p>
      <span className="mt-6 inline-flex items-center gap-1 font-semibold text-volt">Baca artikel <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></span>
    </div>
  </Link>
);

const Pager = ({ page, pages, go }) => {
  if (pages <= 1) return null;
  const btn = "grid h-11 min-w-11 place-items-center rounded-full border px-3 text-sm transition-colors";
  return (
    <nav aria-label="Halaman artikel" data-testid="blog-pagination" className="mt-14 flex flex-wrap items-center justify-center gap-2">
      <button disabled={page <= 1} onClick={() => go(page - 1)} data-testid="blog-page-prev" aria-label="Sebelumnya" className={`${btn} border-white/15 text-zinc-200 hover:border-volt disabled:opacity-30`}><ChevronLeft className="h-4 w-4" /></button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
        <button key={n} onClick={() => go(n)} data-testid={`blog-page-${n}`} aria-current={n === page ? "page" : undefined} className={`${btn} ${n === page ? "border-volt bg-volt font-semibold text-ink" : "border-white/15 text-zinc-200 hover:border-volt"}`}>{n}</button>
      ))}
      <button disabled={page >= pages} onClick={() => go(page + 1)} data-testid="blog-page-next" aria-label="Berikutnya" className={`${btn} border-white/15 text-zinc-200 hover:border-volt disabled:opacity-30`}><ChevronRight className="h-4 w-4" /></button>
    </nav>
  );
};

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const category = params.get("kategori") || "";
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  const [data, setData] = useState(null);
  useSeo({
    title: category ? `Artikel ${category} — Tips Convert Pulsa` : "Blog BagiPulsa — Tips Convert Pulsa & Saldo eWallet",
    description: "Kumpulan artikel dan tips seputar convert pulsa jadi uang, saldo eWallet, rate operator, dan cara aman transaksi pulsa bersama BagiPulsa.",
    path: page > 1 ? `/blog?page=${page}` : "/blog",
  });
  useEffect(() => {
    let alive = true;
    api.get("/articles", { params: { page, limit: 9, category, q } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ items: [], pages: 1, categories: [] }); });
    return () => { alive = false; };
  }, [page, category, q]);
  const update = (next) => {
    const p = new URLSearchParams();
    const merged = { page, kategori: category, q, ...next };
    Object.entries(merged).forEach(([k, v]) => { if (v && !(k === "page" && v === 1)) p.set(k, String(v)); });
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const items = Array.isArray(data?.items) ? data.items : [];
  const cats = Array.isArray(data?.categories) ? data.categories : [];
  const showFeatured = page === 1 && !category && !q && items.length > 0;
  const grid = showFeatured ? items.slice(1) : items;
  return (
    <PageShell testId="blog-page">
      <section className="mx-auto max-w-[1400px] px-5 pb-10 md:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400"><span className="text-volt">Blog</span> — artikel & tips</p>
          <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">Cerita seputar <span className="text-volt">pulsa & cuan.</span></h1>
        </Reveal>
        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:flex-wrap lg:px-0" data-testid="blog-categories">
            {["", ...cats].map((c) => (
              <button key={c || "all"} onClick={() => update({ kategori: c, page: 1 })} data-testid={`blog-cat-${c || "semua"}`} className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${category === c ? "border-volt bg-volt font-semibold text-ink" : "border-white/15 text-zinc-200 hover:border-volt"}`}>{c || "Semua"}</button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); update({ q: term.trim(), page: 1 }); }} className="relative w-full lg:w-80">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input data-testid="blog-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Cari artikel..." className="w-full rounded-full border border-white/15 bg-surface py-3 pl-11 pr-4 text-sm text-paper outline-none focus:border-volt" />
          </form>
        </div>
        {data && items.length === 0 ? <p data-testid="blog-empty" className="mt-16 text-zinc-400">{q || category ? "Tidak ada artikel yang cocok." : "Belum ada artikel."}</p> : null}
        {showFeatured ? <div className="mt-10"><Featured a={items[0]} /></div> : null}
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {grid.map((a) => <ArticleCard key={a.id} a={a} />)}
        </div>
        <Pager page={page} pages={data?.pages || 1} go={(n) => update({ page: n })} />
      </section>
    </PageShell>
  );
}
